<app-data-set
	#dataSet
	[dataSource]="dataSource"
	[selectedDisplayFormat]="formatSelectedUserName"
	[rowId]="rowId"
	[enablePersistentSelection]="true"
>
	<app-data-set-toolbar>
		<div *appSearchActionGroup style="flex: 1">
			<app-data-set-search
				defaultFilterName="userName"
				[defaultFilterLabel]="filterLabels.userName"
				[placeholder]="filterLabels.defaultPlaceholder"
			>
				<div class="filter-grid" [style.--columns]="3">
					<div class="filter-item">
						<mat-form-field subscriptSizing="dynamic" appearance="outline">
							<mat-label>{{ filterLabels.userName }}</mat-label>
							<input
								matInput
								[ngModel]="dataSet.filter('userName')"
								(ngModelChange)="dataSet.filter('userName', filterLabels.userName, $event)"
								data-automation-id="user-name-input"
							/>
						</mat-form-field>
					</div>
					<div class="filter-item">
						<mat-form-field subscriptSizing="dynamic" appearance="outline">
							<mat-label>{{ filterLabels.company }}</mat-label>
							<input
								matInput
								[ngModel]="dataSet.filter('source')"
								(ngModelChange)="dataSet.filter('source', filterLabels.company, $event)"
								data-automation-id="company-name-input"
							/>
						</mat-form-field>
					</div>
					<div class="filter-item">
						<mat-form-field subscriptSizing="dynamic" appearance="outline">
							<mat-label>{{ filterLabels.activeDate }}</mat-label>
							<input
								matInput
								[matDatepicker]="activeDatePicker"
								[ngModel]="dataSet.filter('activeDate')"
								(ngModelChange)="dataSet.filter('activeDate', filterLabels.activeDate, $event)"
								data-automation-id="active-date-input"
							/>
							<mat-datepicker-toggle
								matSuffix
								class="date-picker-primary-blue"
								[for]="activeDatePicker"
								data-automation-id="active-date-toggle"
							></mat-datepicker-toggle>
							<mat-datepicker #activeDatePicker data-automation-id="active-date-datepicker"></mat-datepicker>
						</mat-form-field>
					</div>
				</div>
			</app-data-set-search>
		</div>

		@if (hasDeviceManagementSystemCapability) {
			<div *appActionGroup>
				<app-data-set-action-button
					i18n-label="To grant access|@@grantAccessLabel"
					label="Grant Access"
					[isDisabled]="loadingService.isLoading(getControlAssignmentsLoadingKey) || !finishedLoadingInitData"
					(click)="
						loadingService.isLoading(getControlAssignmentsLoadingKey) || !finishedLoadingInitData
							? null
							: onGrantAccessClicked()
					"
					data-automation-id="user-access-grant-access-button"
				>
				</app-data-set-action-button>
			</div>
		}

		@if (hasDeviceManagementSystemCapability) {
			<div *appSelectedActionGroup>
				<app-data-set-action-button
					[label]="editAccessLabel"
					(click)="onEditControlAssignmentsClicked()"
					data-automation-id="user-access-edit-duration-button"
				>
				</app-data-set-action-button>
			</div>
		}

		@if (hasDeviceManagementSystemCapability) {
			<div *appSelectedActionGroup>
				<app-data-set-action-button
					class="destructive"
					icon="clear_icon"
					label="Remove"
					i18n-label="@@userAccess-remove"
					(click)="onDeleteControlAssignmentsClicked()"
					data-automation-id="user-access-delete-control-assignment-button"
				>
				</app-data-set-action-button>
			</div>
		}
	</app-data-set-toolbar>
	<app-data-set-content>
		<app-data-set-table-view>
			@if (loadingService.isLoading(getControlAssignmentsLoadingKey) || !finishedLoadingInitData) {
				<app-data-set-loading-row />
			}
			<table
				mat-table
				[dataSource]="dataSource"
				matSort
				[hidden]="loadingService.isLoading(getControlAssignmentsLoadingKey) || !finishedLoadingInitData"
			>
				<tr class="mat-row" *matNoDataRow>
					<td class="mat-cell" [colSpan]="displayedColumns.length" i18n="@@dataSetTable-noRecordsFound">
						No records found.
					</td>
				</tr>

				<ng-container matColumnDef="rowSelect">
					<th mat-header-cell *matHeaderCellDef>
						@if (hasDeviceManagementSystemCapability) {
							<mat-checkbox
								[checked]="isSelectedAllChecked()"
								[indeterminate]="isSelectAllIntermediate()"
								(change)="onSelectAllToggle($event.checked)"
								data-automation-id="select-all-checkbox"
							></mat-checkbox>
						}
					</th>
					<td mat-cell *matCellDef="let row">
						@if (isSelectable(row)) {
							<app-data-set-table-row-selection
								[data]="row"
								[appHasSystemCapability]
								has="deviceManagement"
							></app-data-set-table-row-selection>
						}
					</td>
				</ng-container>

				<ng-container matColumnDef="userName">
					<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@userAccessTab-userHeader">User</th>
					<td mat-cell *matCellDef="let row">
						{{ formatUserName(row.entityRelationship.childUser) }}
					</td>
				</ng-container>

				<ng-container matColumnDef="controlName">
					<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@userAccessTab-deviceControlHeader">Control</th>
					<td mat-cell *matCellDef="let row">
						@if (!hasOnlySpecialGrantAccessDevices) {
							<div>
								<div class="resulting-device-access-control-day-time">{{ row.deviceControl | deviceControlDays }}</div>
								<div>
									@if (
										row.deviceControl.permissions.canOpenKeyDoor.value ||
										row.deviceControl.permissions.canReleaseHasp.value
									) {
										<mat-icon
											svgIcon="sl-open-key-door"
											class="capabilities-icon"
											matTooltip="{{ deviceAccessTooltip }}"
										></mat-icon>
									}
									@if (row.deviceControl.permissions.canInstallDevice.value) {
										<mat-icon
											svgIcon="sl-install-device"
											class="capabilities-icon"
											matTooltip="{{ installDeviceTooltip }}"
										></mat-icon>
									}
									@if (row.deviceControl.permissions.canRemoveDevice.value) {
										<mat-icon
											svgIcon="sl-remove-device"
											class="capabilities-icon"
											matTooltip="{{ removeDeviceTooltip }}"
										></mat-icon>
									}
									@if (row.deviceControl.permissions.canArmDisarmAjaxAlarm.value) {
										<mat-icon
											svgIcon="sl-shield-checkmark"
											class="capabilities-icon"
											matTooltip="{{ armDisarmAlarmTooltip }}"
										></mat-icon>
									}
									@if (row.deviceControl.permissions.canOverrideAjaxAlarm.value) {
										<mat-icon
											svgIcon="sl-bell-crossed-out"
											class="capabilities-icon"
											matTooltip="{{ alarmOverrideTooltip }}"
										></mat-icon>
									}
								</div>
							</div>
						}

						<!-- Senturian/PDQ, SentriVault, or mixed special-grant: hide Visit/Install/Remove -->
						@if (hasOnlySpecialGrantAccessDevices) {
							<div>
								<div>{{ row.deviceControl | deviceControlDaysOnly }}</div>
								<div>{{ row.deviceControl | deviceControlHoursOnly }}</div>
								@if (row.deviceControl.permissions.canArmDisarmAjaxAlarm.value) {
									<mat-icon
										svgIcon="sl-shield-checkmark"
										class="capabilities-icon"
										matTooltip="{{ armDisarmAlarmTooltip }}"
									></mat-icon>
								}
								@if (row.deviceControl.permissions.canOverrideAjaxAlarm.value) {
									<mat-icon
										svgIcon="sl-bell-crossed-out"
										class="capabilities-icon"
										matTooltip="{{ alarmOverrideTooltip }}"
									></mat-icon>
								}
							</div>
						}
					</td>
				</ng-container>

				<ng-container matColumnDef="source">
					<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@userAccessTab-companyNameHeader">Company</th>
					<td mat-cell *matCellDef="let row">
						{{ row.entityRelationship?.parentEntity?.name ? row.entityRelationship.parentEntity.name : '' }}
					</td>
				</ng-container>

				<ng-container matColumnDef="deviceGroupName">
					<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@userAccessTab-deviceGroupHeader">
						Device Group
					</th>
					<td mat-cell *matCellDef="let row">{{ row.deviceGroup.name }}</td>
				</ng-container>

				<ng-container matColumnDef="duration">
					<!-- This is in the user's local timezone (not UTC) because UTC dates give incorrect user assumptions
          and we don't know the timezone to use if there are multiple timezones per device in device group -->
					<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@userAccessTab-durationHeader">Duration</th>
					<td mat-cell *matCellDef="let row">
						{{ row | controlAssignmentDuration: localizeInherited : localizeTo : localizeNone }}
					</td>
				</ng-container>

				<ng-container matColumnDef="rowMenu">
					<th mat-header-cell *matHeaderCellDef></th>
					<td mat-cell *matCellDef="let row">
						<!-- Rules for allowing users to edit/delete the accesses:
            1. They are allowed if the user has the device management system capability
            2. They are allowed if they are from the same company that created the access record
            3. They are NOT allowed if looking at device group accesses from the DEVICE access page and not the DEVICE GROUP access page

            If editing this, make sure the countTotalSelectableElements() and onSelectAllToggle() logic is modified as needed.
            -->
						@if (hasDeviceManagementSystemCapability && isSelectable(row)) {
							<app-data-set-table-menu [menu]="menu" [row]="row"></app-data-set-table-menu>
						}
					</td>
				</ng-container>

				<tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
				<tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
			</table>

			<mat-menu #menu="matMenu">
				<ng-template matMenuContent let-row="row">
					<button
						mat-menu-item
						(click)="onRowMenuEditClicked(row)"
						class="row-menu-edit"
						data-automation-id="user-access-edit-duration-two-button"
					>
						{{ editAccessLabel }}
					</button>
					<button
						mat-menu-item
						(click)="onRowMenuDeleteClicked(row)"
						i18n="@@userAccess-removeButton"
						data-automation-id="user-access-delete-two-button"
					>
						Remove
					</button>
				</ng-template>
			</mat-menu>
		</app-data-set-table-view>
	</app-data-set-content>
</app-data-set>
